import {
  ArrowLeftRight,
  ChartColumn,
  FolderKanban,
  LayoutDashboard,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Plus,
  Receipt,
  Settings,
  Sun,
  Tags,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { useAuthStore, useCurrentUser } from '@/features/auth/store';
import { useExpenseEditor } from '@/features/expenses/editor';
import { useActiveGroup } from '@/features/groups/store';
import { useSettingsStore, type ThemeMode } from '@/features/settings/store';
import { cn } from '@/lib/cn';
import { Avatar, Button, IconButton } from '@/ui';

import { Brand, BrandMark } from './Brand';
import { GroupSwitcher } from './GroupSwitcher';

type NavItemSpec = { to: string; label: string; icon: LucideIcon; end?: boolean };

const NAV: NavItemSpec[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/expenses', label: 'Expenses', icon: Receipt },
  { to: '/settle', label: 'Settle up', icon: ArrowLeftRight },
  { to: '/reports', label: 'Reports', icon: ChartColumn },
];

const MANAGE: NavItemSpec[] = [
  { to: '/groups', label: 'Groups', icon: FolderKanban },
  { to: '/members', label: 'Members', icon: Users },
  { to: '/categories', label: 'Categories', icon: Tags },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const THEME_OPTIONS: { value: ThemeMode; icon: LucideIcon; label: string }[] = [
  { value: 'light', icon: Sun, label: 'Light theme' },
  { value: 'dark', icon: Moon, label: 'Dark theme' },
  { value: 'system', icon: Monitor, label: 'Follow system theme' },
];

export function AppShell() {
  const openNew = useExpenseEditor((state) => state.openNew);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Global shortcuts. Ignored while the user is typing so they never swallow a
  // literal "n" or "/" in a form field.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const typing =
        target?.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '');
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'n') {
        event.preventDefault();
        openNew();
      } else if (event.key === '/') {
        event.preventDefault();
        document.querySelector<HTMLInputElement>('[data-search-input]')?.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openNew]);

  return (
    <div className="flex min-h-screen">
      <aside className="sticky top-0 hidden h-screen w-[264px] shrink-0 border-r border-line bg-surface md:block">
        <SidebarContent />
      </aside>

      {drawerOpen && <MobileDrawer onClose={() => setDrawerOpen(false)} />}

      <div className="flex min-w-0 flex-1 flex-col">
        <MobileTopBar onMenu={() => setDrawerOpen(true)} />
        <main className="mx-auto w-full max-w-[1200px] flex-1 px-4 pb-28 pt-6 sm:px-6 md:px-10 md:pb-12 md:pt-10">
          <Outlet />
        </main>
        <MobileTabBar onAdd={openNew} />
      </div>
    </div>
  );
}

/** Shared by the desktop sidebar and the mobile drawer. */
function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const openNew = useExpenseEditor((state) => state.openNew);

  return (
    <div className="flex h-full flex-col overflow-y-auto scroll-thin p-4">
      <div className="mb-5 px-1 pt-1">
        <Brand />
      </div>

      <GroupSwitcher />

      <Button
        icon={Plus}
        className="mt-3 w-full"
        onClick={() => {
          onNavigate?.();
          openNew();
        }}
      >
        Add expense
      </Button>

      <nav className="mt-6 flex flex-1 flex-col gap-1">
        <SectionLabel>Overview</SectionLabel>
        {NAV.map((item) => (
          <NavItem key={item.to} {...item} onNavigate={onNavigate} />
        ))}

        <SectionLabel className="mt-5">Manage</SectionLabel>
        {MANAGE.map((item) => (
          <NavItem key={item.to} {...item} onNavigate={onNavigate} />
        ))}
      </nav>

      <AccountCard />
      <ThemeSwitch />
    </div>
  );
}

/** Who is signed in, with the way out. */
function AccountCard() {
  const user = useCurrentUser();
  const signOut = useAuthStore((state) => state.signOut);
  const [pictureFailed, setPictureFailed] = useState(false);

  if (!user) return null;

  return (
    <div className="mt-5 flex items-center gap-3 rounded-2xl border border-line p-2.5">
      {user.pictureUrl && !pictureFailed ? (
        <img
          src={user.pictureUrl}
          alt=""
          // Google profile photos refuse hot-linking when a referrer is sent.
          referrerPolicy="no-referrer"
          onError={() => setPictureFailed(true)}
          className="size-8 shrink-0 rounded-full object-cover"
        />
      ) : (
        <Avatar name={user.name} id={user.id} size="sm" />
      )}
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[13px] font-semibold">{user.name}</span>
        <span className="block truncate text-[11px] text-muted">{user.email}</span>
      </span>
      <IconButton icon={LogOut} label="Sign out" size="sm" onClick={signOut} />
    </div>
  );
}

function SectionLabel({ children, className }: { children: string; className?: string }) {
  return (
    <p
      className={cn(
        'mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted/80',
        className
      )}
    >
      {children}
    </p>
  );
}

function NavItem({
  to,
  label,
  icon: Icon,
  end,
  onNavigate,
}: NavItemSpec & { onNavigate?: () => void }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition',
          isActive
            ? 'bg-primary/10 font-semibold text-primary'
            : 'font-medium text-muted hover:bg-elevated hover:text-fg'
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className="absolute -left-4 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary"
              aria-hidden
            />
          )}
          <Icon size={18} strokeWidth={isActive ? 2.3 : 2} />
          {label}
        </>
      )}
    </NavLink>
  );
}

function ThemeSwitch() {
  const themeMode = useSettingsStore((state) => state.themeMode);
  const setThemeMode = useSettingsStore((state) => state.setThemeMode);

  return (
    <div className="mt-5 flex items-center justify-between gap-2 rounded-2xl bg-elevated/70 p-1.5 pl-3">
      <span className="text-[12px] font-medium text-muted">Theme</span>
      <div className="flex gap-0.5">
        {THEME_OPTIONS.map((option) => (
          <IconButton
            key={option.value}
            icon={option.icon}
            label={option.label}
            size="sm"
            variant="ghost"
            aria-pressed={themeMode === option.value}
            className={cn(
              themeMode === option.value &&
                'bg-surface text-primary shadow-[0_1px_3px_rgb(0_0_0/0.1)] hover:bg-surface hover:text-primary'
            )}
            onClick={() => setThemeMode(option.value)}
          />
        ))}
      </div>
    </div>
  );
}

/** Slide-over copy of the sidebar for narrow screens. */
function MobileDrawer({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      <div
        className="absolute inset-0 animate-fade-in bg-[#0a0c18]/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className="absolute inset-y-0 left-0 w-[288px] max-w-[85vw] animate-drawer-in bg-surface shadow-pop"
      >
        <SidebarContent onNavigate={onClose} />
      </aside>
    </div>
  );
}

function MobileTopBar({ onMenu }: { onMenu: () => void }) {
  const group = useActiveGroup();

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-bg/85 px-4 backdrop-blur-md md:hidden">
      <IconButton icon={Menu} label="Open menu" onClick={onMenu} />
      <BrandMark className="size-8 rounded-[10px]" />
      <button
        type="button"
        onClick={onMenu}
        className="ml-auto flex min-w-0 items-center gap-2 rounded-full border border-line bg-surface py-1.5 pl-2 pr-3 text-[13px] font-medium shadow-card"
      >
        <span
          className="size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: group?.color ?? 'var(--muted)' }}
          aria-hidden
        />
        <span className="truncate">{group?.name ?? 'No group'}</span>
      </button>
    </header>
  );
}

const TAB_BAR: NavItemSpec[] = [
  { to: '/', label: 'Home', icon: LayoutDashboard, end: true },
  { to: '/expenses', label: 'Expenses', icon: Receipt },
  { to: '/settle', label: 'Settle', icon: ArrowLeftRight },
  { to: '/reports', label: 'Reports', icon: ChartColumn },
];

/** Bottom navigation on phones, with the add button raised in the middle. */
function MobileTabBar({ onAdd }: { onAdd: () => void }) {
  const tab = (item: NavItemSpec) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'flex flex-col items-center gap-1 py-2 text-[10px] font-semibold transition',
          isActive ? 'text-primary' : 'text-muted'
        )
      }
    >
      <item.icon size={21} strokeWidth={2.1} />
      {item.label}
    </NavLink>
  );

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden">
      <div className="mx-auto grid max-w-md grid-cols-5 items-end px-2">
        {TAB_BAR.slice(0, 2).map(tab)}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onAdd}
            aria-label="Add expense"
            className="bg-brand -mt-5 mb-1.5 inline-flex size-14 items-center justify-center rounded-2xl text-white shadow-[0_10px_24px_-6px_rgb(79_70_229/0.6)] ring-4 ring-bg transition active:scale-95"
          >
            <Plus size={26} strokeWidth={2.5} />
          </button>
        </div>
        {TAB_BAR.slice(2).map(tab)}
      </div>
    </nav>
  );
}
